import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SourceChannelCreateInput, SourceChannelDto, SourceChannelUpdateInput } from '@upme/contracts';
import { api } from '../../app/api-client';
import { sourceChannelByIdOptions, sourceChannelsKey } from './queries';

function invalidateList(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: [...sourceChannelsKey, 'list'] });
}

export function useCreateSourceChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SourceChannelCreateInput) =>
      api.post('admin/source-channels', { json: body }).json<SourceChannelDto>(),
    onSuccess: () => invalidateList(qc),
  });
}

export function useUpdateSourceChannel(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SourceChannelUpdateInput) =>
      api.patch(`admin/source-channels/${id}`, { json: body }).json<SourceChannelDto>(),
    onSuccess: (data) => {
      qc.setQueryData(sourceChannelByIdOptions(id).queryKey, data);
      return invalidateList(qc);
    },
  });
}

export function useDeleteSourceChannel() {
  const qc = useQueryClient();
  return useMutation({
    // ?hard=true обязателен: без него контроллер отбивает запрос 400-й
    // (soft-disable делается через PATCH isActive=false, а не DELETE).
    mutationFn: (id: string) => api.delete(`admin/source-channels/${id}`, { searchParams: { hard: 'true' } }),
    onSuccess: () => invalidateList(qc),
  });
}

export function useFetchSourceChannelNow(id: string) {
  return useMutation({
    mutationFn: () => api.post(`admin/source-channels/${id}/fetch-now`),
  });
}

export function useDetachSourceChannelMessages(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`admin/source-channels/${id}/detach-messages`).json<{ detached: number }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sourceChannelByIdOptions(id).queryKey });
      qc.invalidateQueries({ queryKey: ['admin', 'import-queue'] });
      return invalidateList(qc);
    },
  });
}
